import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { getSupabaseBrowserClient } from '@/lib/supabase';

function callbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

// Pilot toggle: provider buttons are env-gated. Default is Google-only.
// Microsoft code path is preserved (not deleted) so Phase 2 can re-enable
// it by flipping VITE_ENABLE_MICROSOFT_AUTH=true with no code change.
const enableGoogle = import.meta.env.VITE_ENABLE_GOOGLE_AUTH !== 'false';
const enableMicrosoft = import.meta.env.VITE_ENABLE_MICROSOFT_AUTH === 'true';

export default function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'google' | 'azure' | null>(null);
  const supabase = getSupabaseBrowserClient();
  const isDev = import.meta.env.DEV;

  async function signInWith(provider: 'google' | 'azure') {
    setError(null);
    if (!supabase) {
      setError(
        'Sign-in is not configured yet. Ask your administrator to set up the app.',
      );
      return;
    }
    setBusy(provider);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: callbackUrl(),
        skipBrowserRedirect: false,
      },
    });
    setBusy(null);
    if (oauthError) {
      // Map the specific Supabase "provider not enabled" failures to a
      // pilot-friendly message. The raw error text leaks internal config
      // ("validation_failed, unsupported provider, provider is not enabled")
      // which is confusing to receptionists. Everything else falls through
      // to the existing path so unknown errors are still surfaced.
      const raw = oauthError.message ?? '';
      const lower = raw.toLowerCase();
      if (
        lower.includes('provider is not enabled') ||
        lower.includes('unsupported provider')
      ) {
        setError(
          'Google sign-in is not enabled yet. Please contact your AdoraCare admin.',
        );
      } else {
        setError(raw);
      }
    }
  }

  return (
    <main className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-lg flex-col justify-center px-4 py-10">
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            {enableMicrosoft
              ? 'Use your work Google or Microsoft account. You will be redirected to sign in securely, then brought back here.'
              : 'Use your work Google account. You will be redirected to sign in securely, then brought back here.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {enableGoogle ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-center gap-2"
                disabled={!supabase || busy !== null}
                onClick={() => void signInWith('google')}
              >
                {busy === 'google' ? 'Redirecting…' : 'Continue with Google'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Use your Partha Dental Google account.
              </p>
            </>
          ) : null}
          {enableMicrosoft ? (
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center gap-2"
              disabled={!supabase || busy !== null}
              onClick={() => void signInWith('azure')}
            >
              {busy === 'azure' ? 'Redirecting…' : 'Continue with Microsoft'}
            </Button>
          ) : null}
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {isDev ? (
            <details className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
              <summary className="cursor-pointer font-medium text-foreground">
                Developer setup
              </summary>
              <p className="mt-2">
                In Supabase: Authentication → Providers (enable Google and Azure).
                Add this exact redirect URL (must match how you open the app —
                <code className="mx-0.5 rounded bg-muted px-1">localhost</code> vs{' '}
                <code className="mx-0.5 rounded bg-muted px-1">127.0.0.1</code> are
                different):
              </p>
              <code className="mt-1 block break-all rounded bg-muted px-2 py-1.5">
                {typeof window !== 'undefined' ? callbackUrl() : '…/auth/callback'}
              </code>
              <p className="mt-2">
                Env: <code className="rounded bg-muted px-1">VITE_SUPABASE_URL</code>,{' '}
                <code className="rounded bg-muted px-1">VITE_SUPABASE_ANON_KEY</code>.
                API routes still use server auth until JWT verification is wired (
                <code className="rounded bg-muted px-1">docs/AUTH-PROVIDER-AGNOSTIC-PLAN.md</code>
                ).
              </p>
            </details>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-col gap-2 border-t pt-4 text-sm text-muted-foreground">
          <p>Having trouble signing in? Contact your AdoraCare administrator.</p>
          <Link to="/home" className="text-center text-primary underline-offset-4 hover:underline">
            Back to home
          </Link>
        </CardFooter>
      </Card>
    </main>
  );
}
